import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Radio } from '@atlaskit/radio';
import Textfield from '@atlaskit/textfield';
import { FormValues, HowHeard } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  howHeard?: string;
  howHeardOther?: string;
  howHeardEmployee?: string;
  referrerName?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

const OPTIONS: { id: NonNullable<HowHeard>; label: string }[] = [
  { id: 'ea', label: 'Atlassian Enterprise Advocate (EA)' },
  { id: 'tam', label: 'Atlassian Technical Account Manager (TAM)' },
  { id: 'csm', label: 'Atlassian Customer Success Manager' },
  { id: 'employee', label: 'Atlassian employee' },
  { id: 'champion', label: 'Atlassian Champion' },
  { id: 'solution_partner', label: 'Atlassian Solution Partner' },
  { id: 'community_website', label: 'Atlassian Community website' },
  { id: 'email_from_atlassian', label: 'Email from Atlassian' },
  { id: 'corporate_event', label: 'Atlassian corporate event (for example, Team)' },
  { id: 'social_media', label: 'Social media (for example, Facebook, LinkedIn, X)' },
  { id: 'coworker', label: 'Someone I work with' },
  { id: 'other_user_group', label: 'Another user group (Agile, DevOps, project management, etc.)' },
  { id: 'other', label: 'Other (please specify)' },
];

export const HowHeardSection: React.FC<Props> = ({ values, updateField, errors }) => {
  const setHowHeard = (id: HowHeard) => () => updateField('howHeard', id);

  const showOtherField = values.howHeard === 'other';
  const showEmployeeField = values.howHeard === 'employee';

  return (
    <div css={sectionBoxStyles}>
      <div
        css={fieldStyles}
        className={
          errors?.howHeard
            ? 'champion-form-how-heard champion-form-how-heard--error'
            : 'champion-form-how-heard'
        }
      >
        <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>{' '}
          How did you find out about the Atlassian Champions program?
        </label>

        <div css={controlTopStyles}>
          {OPTIONS.map((opt) => (
            <Radio
              key={opt.id}
              name="how_heard"
              label={opt.label}
              value={opt.id}
              isChecked={values.howHeard === opt.id}
              onChange={setHowHeard(opt.id)}
            />
          ))}
        </div>

        {errors?.howHeard && <div css={errorStyles}>{errors.howHeard}</div>}
      </div>

      {showOtherField && (
        <div css={fieldTopStyles} className="champion-form-how-heard-other">
          <label css={[labelStyles, salesforceLabelStyles]} htmlFor="how-heard-other">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>{' '}
            Please specify
          </label>
          <Textfield
            id="how-heard-other"
            value={values.howHeardOther}
            onChange={(e) => updateField('howHeardOther', (e.target as HTMLInputElement).value)}
            isCompact
            isInvalid={!!errors?.howHeardOther}
          />
          {errors?.howHeardOther && <div css={errorStyles}>{errors.howHeardOther}</div>}
        </div>
      )}

      {showEmployeeField && (
        <div css={fieldTopStyles} className="champion-form-how-heard-employee">
          <label css={[labelStyles, salesforceLabelStyles]} htmlFor="how-heard-employee">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>{' '}
            Please specify
          </label>
          <Textfield
            id="how-heard-employee"
            placeholder="Enter details"
            value={values.howHeardEmployee}
            onChange={(e) => updateField('howHeardEmployee', (e.target as HTMLInputElement).value)}
            isCompact
            isInvalid={!!errors?.howHeardEmployee}
          />
          {errors?.howHeardEmployee && <div css={errorStyles}>{errors.howHeardEmployee}</div>}
        </div>
      )}

      {values.howHeard === 'champion' && (
        <div css={fieldTopStyles} className="champion-form-how-heard-referrer">
          <label css={[labelStyles, salesforceLabelStyles]} htmlFor="referrer-name">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>{' '}
            Referrer Name
          </label>
          <Textfield
            id="referrer-name"
            placeholder="Enter name"
            value={values.referrerName}
            maxLength={80}
            onChange={(e) => updateField('referrerName', (e.target as HTMLInputElement).value)}
            isCompact
            isInvalid={!!errors?.referrerName}
          />
          {errors?.referrerName && <div css={errorStyles}>{errors.referrerName}</div>}
        </div>
      )}
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '1rem',
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '1rem',
});

const labelStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

const salesforceLabelStyles = css({
  color: '#696969',
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  fontWeight: 400,
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

const labelBoldStyles = css({
  fontWeight: 600,
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
